import { Button } from '@affine/component';
import { notify } from '@affine/component/ui/notification';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  type AuthMethod,
  getLastAuthMethod,
  setLastAuthMethod,
} from '@affine/core/components/sign-in/sign-in';
import { AuthService, ServerService } from '@affine/core/modules/cloud';
import { UrlService } from '@affine/core/modules/url';
import { UserFriendlyError } from '@affine/error';
import { OAuthProviderType } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import { GithubIcon, LockIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import {
  type ReactElement,
  type SVGAttributes,
  useCallback,
  useMemo,
} from 'react';

import * as styles from './oauth.css';

type AuthI18n = ReturnType<typeof useI18n>;
type ErrorTranslationFn = (data?: unknown) => string;

type OAuthProviderLabelKey =
  | 'com.streamlain.auth.oauth.provider.apple'
  | 'com.streamlain.auth.oauth.provider.github'
  | 'com.streamlain.auth.oauth.provider.google'
  | 'com.streamlain.auth.oauth.provider.microsoft'
  | 'com.streamlain.auth.oauth.provider.oidc';

const OAuthProviderMap: Record<
  OAuthProviderType,
  {
    labelKey: OAuthProviderLabelKey;
    icon: ReactElement<SVGAttributes<SVGElement>>;
  }
> = {
  [OAuthProviderType.Google]: {
    labelKey: 'com.streamlain.auth.oauth.provider.google',
    icon: <GoogleBrandIcon />,
  },

  [OAuthProviderType.GitHub]: {
    labelKey: 'com.streamlain.auth.oauth.provider.github',
    icon: <GithubIcon />,
  },

  [OAuthProviderType.Microsoft]: {
    labelKey: 'com.streamlain.auth.oauth.provider.microsoft',
    icon: <MicrosoftIcon />,
  },

  [OAuthProviderType.OIDC]: {
    labelKey: 'com.streamlain.auth.oauth.provider.oidc',
    icon: <LockIcon />,
  },

  [OAuthProviderType.Apple]: {
    labelKey: 'com.streamlain.auth.oauth.provider.apple',
    icon: <AppleIcon />,
  },
};

const providerToMethod: Record<OAuthProviderType, AuthMethod> = {
  [OAuthProviderType.Google]: 'google',
  [OAuthProviderType.GitHub]: 'github',
  [OAuthProviderType.Microsoft]: 'microsoft',
  [OAuthProviderType.OIDC]: 'oidc',
  [OAuthProviderType.Apple]: 'apple',
};

const providerDisplayOrder: Record<OAuthProviderType, number> = {
  [OAuthProviderType.Google]: 0,
  [OAuthProviderType.Apple]: 1,
  [OAuthProviderType.Microsoft]: 2,
  [OAuthProviderType.GitHub]: 3,
  [OAuthProviderType.OIDC]: 4,
};

function getAuthErrorMessage(t: AuthI18n, error: UserFriendlyError): string {
  const key = `error.${error.name}` as keyof AuthI18n;
  const translate = t[key];
  if (typeof translate === 'function') {
    return (translate as ErrorTranslationFn)(error.data);
  }
  return t['com.streamlain.auth.toast.message.unexpected']();
}

export function OAuth({
  redirectUrl,
  email,
}: {
  redirectUrl?: string;
  email?: string;
}) {
  const serverService = useService(ServerService);
  const urlService = useService(UrlService);
  const oauth = useLiveData(serverService.server.features$.map(r => r?.oauth));
  const oauthProviders = useLiveData(
    serverService.server.config$.map(r => r?.oauthProviders)
  );
  const auth = useService(AuthService);
  const t = useI18n();

  const lastMethod = useMemo(() => getLastAuthMethod(), []);
  const orderedOAuthProviders = useMemo(
    () =>
      [...(oauthProviders ?? [])].sort(
        (left, right) =>
          providerDisplayOrder[left] - providerDisplayOrder[right]
      ),
    [oauthProviders]
  );

  const onContinue = useAsyncCallback(
    async (provider: OAuthProviderType) => {
      setLastAuthMethod(providerToMethod[provider]);
      track.$.$.auth.signIn({ method: 'oauth', provider });

      const open = async () => {
        try {
          const admissionId = email?.trim()
            ? await auth.createAccessAdmission(email, 'oauth')
            : undefined;
          const scheme = BUILD_CONFIG.isNative
            ? urlService.getClientScheme()
            : undefined;
          const options = await auth.oauthPreflight(
            provider,
            scheme ?? 'web',
            redirectUrl,
            true,
            admissionId
          );
          // OAuth follows an async preflight; popup windows are browser-blocked here.
          urlService.openExternal(options.url);
        } catch (e) {
          const error = UserFriendlyError.fromAny(e);
          notify.error({
            title: t['com.streamlain.auth.toast.title.failed'](),
            message: getAuthErrorMessage(t, error),
          });
        }
      };

      await open();
    },
    [urlService, redirectUrl, email, auth, t]
  );

  if (!oauth) {
    return null;
  }

  if (!orderedOAuthProviders.length) {
    return null;
  }

  return (
    <div className={styles.providerList}>
      {orderedOAuthProviders.map(provider => {
        return (
          <OAuthProvider
            key={provider}
            provider={provider}
            onContinue={onContinue}
            isLastUsed={lastMethod === providerToMethod[provider]}
          />
        );
      })}
    </div>
  );
}

function MicrosoftIcon(props: SVGAttributes<SVGElement>) {
  return (
    <svg viewBox="0 0 21 21" aria-hidden="true" focusable="false" {...props}>
      <rect x="1" y="1" width="9" height="9" fill="currentColor" />
      <rect x="11" y="1" width="9" height="9" fill="currentColor" />
      <rect x="1" y="11" width="9" height="9" fill="currentColor" />
      <rect x="11" y="11" width="9" height="9" fill="currentColor" />
    </svg>
  );
}

function GoogleBrandIcon(props: SVGAttributes<SVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
      />
    </svg>
  );
}

function AppleIcon(props: SVGAttributes<SVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...props}>
      <path
        fill="currentColor"
        d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
      />
    </svg>
  );
}

interface OauthProviderProps {
  provider: OAuthProviderType;
  onContinue: (provider: OAuthProviderType) => void;
  isLastUsed?: boolean;
}

function OAuthProvider({
  onContinue,
  provider,
  isLastUsed,
}: OauthProviderProps) {
  const t = useI18n();
  const { icon, labelKey } =
    provider in OAuthProviderMap
      ? OAuthProviderMap[provider]
      : { icon: undefined, labelKey: undefined };
  const label = labelKey ? t[labelKey]() : provider;

  const onClick = useCallback(() => {
    onContinue(provider);
  }, [onContinue, provider]);

  const accessibleLabel = isLastUsed
    ? t['com.streamlain.auth.oauth.continue-with-provider.last-used']({
        provider: label,
      })
    : t['com.streamlain.auth.oauth.continue-with-provider']({
        provider: label,
      });

  return (
    <Button
      className={styles.providerButton}
      size="custom"
      variant="secondary"
      prefix={icon}
      prefixClassName={
        provider === OAuthProviderType.Apple
          ? styles.appleProviderIcon
          : provider === OAuthProviderType.Microsoft
            ? styles.microsoftProviderIcon
            : styles.providerIcon
      }
      aria-label={accessibleLabel}
      tooltip={accessibleLabel}
      onClick={onClick}
    />
  );
}
